import {
  ArrowDownIcon,
  CheckIcon,
  CloseIcon,
  RightIcon,
  UnCheckIcon,
} from '@/components/icons'
import { AreaType } from '@/types/areaZone'
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Menu,
  Stack,
  Typography,
} from '@mui/material'
import Divider from '@mui/material/Divider'
import { useState, useMemo } from 'react'
import classes from './SensorHeader.module.scss'
import { useAppDispatch, useAppSelector } from '@store/stores'
import {
  stationGeoTagDeviceSliceActions,
  stationGeoTagDeviceState,
  groupAreasAndSubAreas,
} from '@store/slices/StataionGeoTagDevice/stationGeoTagDeviceSlice'
import _ from 'lodash'
import useTranslation from '@/hooks/useTranslation'

interface MenuFilterProps {
  anchorEl?: null | HTMLElement
  onClose?: () => void
  handleFilterArea?: () => void
}

const MenuFilterSiteLocationV2 = ({
  anchorEl,
  onClose,
  handleFilterArea,
}: MenuFilterProps) => {
  const { t } = useTranslation()

  const open = Boolean(anchorEl)
  const [openDrop, setOpenDrop] = useState<{ [key: number]: boolean }>({})

  const dispatch = useAppDispatch()
  const stationGeoTagDevice = useAppSelector(stationGeoTagDeviceState)

  const handleChangeArea = (area: number) => {
    dispatch(
      stationGeoTagDeviceSliceActions.changeArea({
        value: area,
      }),
    )
  }

  const handleChangeSubArea = (subAreaId: number, areaId: number) => {
    dispatch(
      stationGeoTagDeviceSliceActions.changeAreaSub({
        value: {
          areaId,
          subAreaId,
        },
      }),
    )
  }

  const handleFilterUpdate = () => {
    dispatch(
      stationGeoTagDeviceSliceActions.setSubAreaFilterRaw({
        value: stationGeoTagDevice.areas,
      }),
    )
    if (_.isFunction(handleFilterArea)) handleFilterArea()
  }

  const handleClearAreaSub = () => {
    dispatch(stationGeoTagDeviceSliceActions.resetArea())
  }

  const areas = useMemo(() => {
    return groupAreasAndSubAreas(stationGeoTagDevice.areas || [])
  }, [stationGeoTagDevice.areas])

  return (
    <Menu
      id="demo-positioned-menu"
      aria-labelledby="basic-button"
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      sx={{
        padding: 0,
        borderRadius: '5px',
        border: '1px solid #D7D7D7',
        boxShadow: '0px 2px 2px rgba(0, 0, 0, 0.15)',
      }}
    >
      <Box
        sx={{
          width: '268px',
          padding: '7px 16px 20px 16px',
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Typography color="text.main" className={classes.textSiteLocation}>
            {t('stations.filters.filterAreaSubArea')}
          </Typography>
          <Box color="text.main">
            <CloseIcon onClick={onClose} cursor="pointer" />
          </Box>
        </Stack>

        <Stack>
          <Box className={classes.groupSiteLocation}>
            <Stack
              sx={{
                overflowY: 'scroll',
                borderRadius: '5px',
                height: '195px',
                '&::-webkit-scrollbar': {
                  width: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#A5A5A5',
                  borderRadius: '5px',
                },
              }}
            >
              {areas.map((row: AreaType) => {
                const hasSubAreas = (row.subAreas || []).length > 0
                const isExpanded = Boolean(openDrop[row.id])
                const isChecked = hasSubAreas
                  ? row.subAreas.every((subs: AreaType) => subs.isSelected)
                  : (row.isSelected ?? false)

                return (
                  <Box key={row.id} sx={{ paddingBottom: '8px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {hasSubAreas ? (
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() =>
                            setOpenDrop((pre) => ({
                              ...pre,
                              [row.id]: !pre[row.id],
                            }))
                          }
                          sx={{ padding: 0, margin: '0px 6px 0px 4px' }}
                        >
                          {isExpanded ? (
                            <ArrowDownIcon
                              sx={{
                                color: 'text.main',
                              }}
                            />
                          ) : (
                            <RightIcon
                              sx={{
                                color: 'text.main',
                              }}
                            />
                          )}
                        </IconButton>
                      ) : (
                        <Box
                          sx={{
                            width: 20,
                            minWidth: 20,
                            margin: '0px 6px 0px 4px',
                          }}
                        />
                      )}
                      <FormControlLabel
                        label={row.name}
                        control={
                          <Checkbox
                            checked={isChecked}
                            onChange={() => handleChangeArea(row.id)}
                            indeterminate={row.isIndeterminate ?? false}
                            checkedIcon={
                              <CheckIcon sx={{ color: '#004744 !important' }} />
                            }
                            icon={<UnCheckIcon />}
                            sx={{
                              padding: 0,
                              margin: '0px 11px 2px 11px',
                              '& .MuiSvgIcon-root': {
                                fontSize: 16,
                                color: 'text.main',
                                mt: '3px',
                              },
                            }}
                          />
                        }
                        sx={{
                          alignItems: 'flex-start',
                          '& .MuiTypography-root': {
                            color: 'text.main',
                            fontWeight: '500',
                            lineHeight: '16px',
                            mt: '3px',
                          },
                        }}
                      />
                    </Box>

                    {/* SubAreas */}
                    {hasSubAreas && (
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            paddingTop: '9px',
                            marginLeft: '36px',
                          }}
                        >
                          {row.subAreas?.map((item: AreaType) => (
                            <FormControlLabel
                              key={item.id}
                              label={item.name}
                              sx={{
                                paddingBottom: '8px',
                                '& .MuiSvgIcon-root': {
                                  fontSize: 16,
                                  color: 'text.main',
                                },
                                '& .MuiTypography-root': {
                                  color: 'text.main',
                                  lineHeight: '16px',
                                  fontWeight: '500',
                                },
                              }}
                              control={
                                <Checkbox
                                  checked={item.isSelected || false}
                                  onChange={() =>
                                    handleChangeSubArea(item.id, row.id)
                                  }
                                  sx={{
                                    padding: 0,
                                    margin: '0px 11px 2px 11px',
                                  }}
                                  checkedIcon={
                                    <CheckIcon
                                      sx={{ color: '#004744 !important' }}
                                    />
                                  }
                                  icon={<UnCheckIcon />}
                                />
                              }
                            />
                          ))}
                        </Box>
                      </Collapse>
                    )}
                  </Box>
                )
              })}
            </Stack>
          </Box>
        </Stack>
      </Box>
      <Divider sx={{ width: '100%' }} />
      <Stack
        justifyContent="space-between"
        px="16px"
        pt="10px"
        pb="10px"
        direction="row"
      >
        <Button
          variant="secondary"
          className={classes.btnReset}
          onClick={handleClearAreaSub}
        >
          {t('commonActionsReset')}
        </Button>
        <Button
          variant="primary"
          className={classes.btnFilter}
          onClick={handleFilterUpdate}
        >
          {t('commonActionsFilter')}
        </Button>
      </Stack>
    </Menu>
  )
}

export default MenuFilterSiteLocationV2
